import React, { useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Button } from 'rebass';
import { Input, Label } from '@rebass/forms';
import classNames from 'classnames';
import { FiCheckCircle, FiLock } from 'react-icons/fi';

import { useForm } from 'hooks/useForm';
import { Card, CardBody } from 'components/Card';
import { Skeleton } from 'components/Skeleton';
import { usePaymentEmailVerifyQuery } from 'api/usePaymentEmailVerifyQuery';
import { usePaymentEmailChangeMutation } from 'api/usePaymentEmailChangeMutation';

/**
 * Self-service page for panelists to update their PayPal payment email.
 * Accessed via a time-limited encrypted link embedded in payment notification emails.
 *
 * Route: /payment-email/change?token=xxx
 * Auth:  None — the encrypted token serves as authentication.
 */
function PaymentEmailChangePage({ location }) {
    /** Encrypted token parsed once from the URL — never changes. */
    const tokenRef = useRef(new URLSearchParams(location.search).get('token') || '');

    /** Client-side or server validation error to display below the input. */
    const [validationError, setValidationError] = useState(null);

    const hasToken = !!tokenRef.current;

    const { register, handleSubmit } = useForm();

    const verifyQuery = usePaymentEmailVerifyQuery(tokenRef.current, {
        enabled: hasToken
    });

    const changeMutation = usePaymentEmailChangeMutation({
        onMutate: () => setValidationError(null),
        onError: error => setValidationError(parseChangeError(error))
    });

    const onSubmit = handleSubmit(data => {
        const email = (data.email || '').trim();
        if (!email) {
            setValidationError('Please enter an email address.');
            return;
        }

        changeMutation.mutate({ token: tokenRef.current, email });
    });

    /** Clear errors as soon as the user resumes typing. */
    const handleInputChange = () => {
        if (validationError) {
            setValidationError(null);
        }
    };

    // ─── Token missing or verification failed ────────────────
    if (!hasToken || verifyQuery.isError) {
        return (
            <PageShell title="Link Expired">
                <div className="text-center">
                    <FiLock size={48} className="mx-auto mb-4 color-danger" aria-hidden="true" />
                    <h1 className="fs-headline-20 color-text-primary mb-2">Link expired or invalid</h1>
                    <p className="fs-body-14 color-text-secondary">
                        This link is invalid or has expired. Please check your email for a valid link, or contact the
                        research team for assistance.
                    </p>
                </div>
            </PageShell>
        );
    }

    // ─── Loading verification ────────────────────────────────
    if (verifyQuery.isLoading) {
        return (
            <PageShell title="Update Payment Email" showLogoSkeleton>
                <p className="sr-only" role="status">
                    Loading payment email form&hellip;
                </p>
                <Skeleton width="70%" height={24} className="!mb-2" />
                <Skeleton width="100%" height={14} className="!mb-1" />
                <Skeleton width="85%" height={14} className="!mb-3" />
                <Skeleton width="40%" height={14} className="!mb-1" />
                <Skeleton width="100%" height={40} className="!mb-3" />
                <Skeleton width="100%" height={40} />
            </PageShell>
        );
    }

    // ─── Change succeeded ────────────────────────────────────
    if (changeMutation.isSuccess) {
        return (
            <PageShell title="Email Updated" branding={verifyQuery.data?.branding}>
                <div className="text-center" role="status" aria-live="polite">
                    <FiCheckCircle size={48} className="mx-auto mb-4 color-success" aria-hidden="true" />
                    <h1 className="fs-headline-20 color-text-primary mb-2">Payment email updated</h1>
                    <p className="fs-body-14 color-text-secondary">
                        Your payment email has been updated to <strong>{changeMutation.data.payment_email}</strong>.
                    </p>
                    <p className="fs-body-14 color-text-secondary">
                        We&apos;ve re-sent your payment notification with the updated details.
                    </p>
                </div>
            </PageShell>
        );
    }

    // ─── Form ────────────────────────────────────────────────
    const { first_name: firstName, payment_email: currentEmail, branding } = verifyQuery.data;
    const greeting = firstName ? `Hi ${firstName},` : 'Hi,';

    return (
        <PageShell title="Update Payment Email" branding={branding}>
            <h1 className="fs-headline-20 color-text-primary mb-2">Update your payment email</h1>
            <p className="fs-body-14 color-text-secondary mb-3">
                {greeting} your payment is currently set to be sent to <strong>{currentEmail}</strong>. Enter a new
                email below if you&apos;d like to change where you receive payment.
            </p>

            <form onSubmit={onSubmit} noValidate>
                <Label htmlFor="payment-email" className="fs-accent-14 color-text-primary mb-1">
                    New payment email
                </Label>

                <Input
                    {...register('email', { onChange: handleInputChange })}
                    id="payment-email"
                    type="email"
                    autoComplete="email"
                    placeholder="your-email@example.com"
                    aria-invalid={!!validationError}
                    aria-describedby={validationError ? 'email-error' : undefined}
                    className={classNames('theme-input', { error: validationError })}
                    disabled={changeMutation.isLoading}
                />

                {validationError && (
                    <p id="email-error" className="fs-body-12 text-danger mt-1" role="alert">
                        {validationError}
                    </p>
                )}

                <Button
                    type="submit"
                    variant="primary"
                    disabled={changeMutation.isLoading}
                    width="100%"
                    className={classNames('!mt-5', { 'opacity-50 cursor-not-allowed': changeMutation.isLoading })}
                    style={
                        branding?.primary_color
                            ? { backgroundColor: branding.primary_color, borderColor: branding.primary_color }
                            : undefined
                    }
                >
                    {changeMutation.isLoading ? 'Updating\u2026' : 'Update payment email'}
                </Button>
            </form>
        </PageShell>
    );
}

// ─── Private helpers ─────────────────────────────────────────

/** Maps API errors to user-friendly messages. Falls back to the raw message for validation errors. */
export function parseChangeError(error) {
    const status = error?.response?.status;
    const data = error?.response?.data;

    if (status === 429) return 'Too many attempts. Please try again later.';
    if (status === 422 && data?.error) return data.error;
    if (status === 422 && data?.message) return data.message;

    return 'Something went wrong. Please try again.';
}

/** Centered card shell shared by all page states. */
function PageShell({ title, branding, showLogoSkeleton, children }) {
    return (
        <>
            <Helmet>
                <title>{title}</title>
            </Helmet>
            <div className="min-h-screen w-full flex justify-center bg-gray-50 p-5 pt-10">
                <div className="max-w-lg w-full">
                    {branding?.logo && (
                        <div className="flex justify-center mb-6">
                            <img src={branding.logo} alt="Logo" className="max-w-[144px] h-auto" />
                        </div>
                    )}
                    {showLogoSkeleton && (
                        <div className="flex justify-center mb-6">
                            <Skeleton width={144} height={144} />
                        </div>
                    )}
                    <Card>
                        <CardBody>{children}</CardBody>
                    </Card>
                </div>
            </div>
        </>
    );
}

export default PaymentEmailChangePage;
